import { Component, ElementRef, Input } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Course, CourseStats } from '../../interfaces/courseInterface';
import { PocketbaseService } from '../../services/pocketbase.service';
import { lastValueFrom, take, timer } from 'rxjs';
import { CommonModule } from '@angular/common';
import { NgIconComponent } from '@ng-icons/core';
import { StatCardComponent } from './stat-card/stat-card.component';
import { ExplainerTextComponent } from '../../components/explainer-text/explainer-text.component';
import { AchievementService } from '../../services/achievement.service';
import { Achievement } from '../../interfaces/achievementInterface';
import { TestService } from '../../services/test.service';
import { AccordionComponent } from '../../components/accordion/accordion.component';
import { AccordionItemComponent } from '../../components/accordion/accordion-item/accordion-item.component';
import { CourseBackgroundImageService } from '../../services/course-background-image.service';

type PurchaseType = 'course' | 'book' | 'bundle';

interface PurchaseOption {
  type: PurchaseType;
  title: string;
  description: string;
  price: number;
  cta: string;
  disabled?: boolean;
  previewUrl?: string;
}

@Component({
    selector: 'app-course-overview',
    imports: [RouterLink, CommonModule, NgIconComponent, StatCardComponent, ExplainerTextComponent, AccordionComponent, AccordionItemComponent],
    templateUrl: './course-overview.component.html',
    styleUrl: './course-overview.component.scss'
})
export class CourseOverviewComponent {
  public modules: { id: string, name: string }[] = [];

  public availableCourses: {
    id: string,
    examId: string,
    name: string,
    access: boolean,
    hasBookAccess?: boolean,
    hasCoursePurchase?: boolean,
    hasBookPurchase?: boolean,
    hasBundlePurchase?: boolean,
    stats?: CourseStats,
    achievements?: Achievement[],
    description?: string,
    topics?: string,
    furtherInformationLink?: string,
    price: number,
    bookPrice?: number,
    bundlePrice?: number,
    bookDownloadUrl?: string,
    bookPdf?: string | string[],
    bookPreviewPdf?: string | string[],
  }[] = [];

  public selectedCourse: {
    id: string,
    examId: string,
    name: string,
    access: boolean,
    hasBookAccess?: boolean,
    hasCoursePurchase?: boolean,
    hasBookPurchase?: boolean,
    hasBundlePurchase?: boolean,
    stats?: CourseStats,
    achievements?: Achievement[],
    description?: string,
    topics?: string,
    furtherInformationLink?: string,
    price: number,
    bookPrice?: number,
    bundlePrice?: number,
    bookDownloadUrl?: string,
    bookPdf?: string | string[],
    bookPreviewPdf?: string | string[],
    lastResults?: { date: Date, results: { id: string, title: string, score: number, timeTaken: number, questionAmount: number, startTime: Date }[] }[]
  } | undefined;
  public isLoading = false;

  @Input()
  public selectedCourseId: string = 'all';
  constructor(private pocketbaseService: PocketbaseService, private achievementService: AchievementService, private testService: TestService, private router: Router, private route: ActivatedRoute, private courseBackgroundImageService: CourseBackgroundImageService) { }

  async ngOnInit() {
    await this.fetchCourses();

    this.route.paramMap.subscribe(params => {
      const courseId = params.get('selectedCourseId');
      const firstCourseId = this.availableCourses[0]?.id;
      this.selectedCourseId = courseId || firstCourseId || 'all';

      if (this.selectedCourseId === 'all') {
        this.selectedCourse = undefined;
        return;
      }

      this.loadCourse(this.selectedCourseId);

      const fragment = this.route.snapshot.fragment;
      if (fragment) {
        setTimeout(() => {
        document.getElementById(fragment)?.scrollIntoView({ behavior: 'smooth' });
        });
      }
    })
  }


  async fetchCourses() {
    this.availableCourses = [];

    const courses = await this.pocketbaseService.pb.collection('courses').getFullList<Course>({
      expand: 'exam'
    });

    const achievements = await this.achievementService.getAchievements();
    const paidPurchasesByCourse = new Map<string, Set<PurchaseType>>();

    const authUserId = this.pocketbaseService.pb.authStore.model?.['id'];

    try {
      if (authUserId) {
        const paidPurchases = await this.pocketbaseService.pb.collection('purchases').getFullList<{ course: string, productType: PurchaseType }>({
          filter: `user = "${authUserId}" && status = "paid" && (productType = "course" || productType = "book" || productType = "bundle")`,
          fields: 'course,productType'
        });

        paidPurchases.forEach((purchase) => {
          const existing = paidPurchasesByCourse.get(purchase.course) ?? new Set<PurchaseType>();
          existing.add(purchase.productType);
          paidPurchasesByCourse.set(purchase.course, existing);
        });
      }
    } catch (error) {
      // Do not block course rendering if purchases cannot be queried.
      console.warn('Could not load paid book purchases', error);
    }

    courses
      .filter(course => !(course.isDemoCourse && !course.access))
      .sort((a, b) => {
        if (a.access === b.access && this.selectedCourseId !== a.id) return 0; // dont change order if they have the same access
        return a.access || this.selectedCourseId === a.id ? -1 : 1; // otherwise, the one that has access or the one that is selected goes first
      }).forEach(course => {
        const tmp = achievements.filter(a => a.exam === course.exam);
        const paidTypes = paidPurchasesByCourse.get(course.id) ?? new Set<PurchaseType>();
        const hasBookPurchase = paidTypes.has('book');
        const hasBundlePurchase = paidTypes.has('bundle');
        const hasCoursePurchase = paidTypes.has('course') || hasBundlePurchase;
        console.log(tmp);
        this.availableCourses.push({
          id: course.id,
          examId: course.exam,
          name: course.name,
          access: course.access,
          hasBookAccess: hasBookPurchase || hasBundlePurchase,
          hasCoursePurchase,
          hasBookPurchase,
          hasBundlePurchase,
          achievements: tmp,
          description: course.description,
          topics: course.topics,
          furtherInformationLink: course.furtherInformationLink,
          price: course.price,
          bookPrice: course.bookPrice,
          bundlePrice: course.bundlePrice,
          bookDownloadUrl: undefined,
          bookPdf: course.bookPdf,
          bookPreviewPdf: course.bookPreviewPdf
        });

      })

    await Promise.all(this.availableCourses.map(async (course) => {
      if (!course.hasBookAccess) {
        return;
      }

      const fileName = this.extractFileName(course.bookPdf);
      if (!fileName) {
        return;
      }

      course.bookDownloadUrl = await this.pocketbaseService.getCourseFileUrl(course.id, fileName, true);
    }));
  }

  selectCourse(courseId: string) {
    this.isLoading = true;
    lastValueFrom(timer(125).pipe(take(1))).then(() => this.isLoading = false);
    this.router.navigate(['/kurse', courseId]);
  }

  async loadCourse(courseId: string) {
    this.selectedCourse = this.availableCourses.find(course => course.id === courseId) || undefined;

    if (this.selectedCourse && this.selectedCourse.access) {
      this.selectedCourse.stats = await this.pocketbaseService.pb.send<CourseStats>(`/stats/${this.selectedCourseId}`, { method: 'GET' });

      const results = await this.testService.getUserTestResults(this.selectedCourse.examId, 1, 5);

      const group: { [key: string]: any[] } = {};

      results.items.forEach(result => {
        const date = new Date(result.test.created).toDateString();
        if (!group[date]) {
          group[date] = [];
        }
        group[date].push({
          id: result.test.id,
          title: result.test.expand.conditions.name,
          score: result.score,
          timeTaken: Math.ceil(result.time_taken) * 1000,
          questionAmount: result.test.expand.conditions.question_amount,
          startTime: new Date(result.test.created)
        })
      })

      this.selectedCourse.lastResults = Object.keys(group).map(date => {
        return {
          date: new Date(date),
          results: group[date]
        }
      });
    }
  }

  // public getCourseCardBackground(courseName: string): string {
  //   return this.courseBackgroundImageService.getCourseCardBackground(courseName);
  // }

  private isPositivePrice(value?: number): value is number {
    return typeof value === 'number' && value > 0;
  }

  private hasBookPdf(bookPdf?: string | string[]): boolean {
    if (Array.isArray(bookPdf)) {
      return bookPdf.length > 0;
    }

    return typeof bookPdf === 'string' && bookPdf.length > 0;
  }

  private extractFileName(file?: string | string[]): string | undefined {
    if (Array.isArray(file)) {
      return file[0];
    }

    return file;
  }

  private getCourseFileUrl(courseId: string, file?: string | string[]): string | undefined {
    const fileName = this.extractFileName(file);
    if (!fileName) {
      return undefined;
    }

    return `${this.pocketbaseService.pb.baseUrl}/api/files/courses/${courseId}/${fileName}`;
  }

  private getBookPreviewUrl(course: { id: string, bookPreviewPdf?: string | string[] }): string | undefined {
    return this.getCourseFileUrl(course.id, course.bookPreviewPdf);
  }

  public getBookDownloadUrl(course?: { id: string, hasBookAccess?: boolean, bookDownloadUrl?: string }): string | undefined {
    if (!course?.hasBookAccess) {
      return undefined;
    }

    return course.bookDownloadUrl;
  }

  public hasBookDownloadAccess(course?: { id: string, hasBookAccess?: boolean, bookDownloadUrl?: string }): boolean {
    return !!this.getBookDownloadUrl(course);
  }

  public hasPurchaseOptions(course?: {
    id: string,
    name: string,
    access: boolean,
    price: number,
    bookPrice?: number,
    bundlePrice?: number,
    hasCoursePurchase?: boolean,
    hasBookPurchase?: boolean,
    hasBundlePurchase?: boolean,
    bookPdf?: string | string[],
    bookPreviewPdf?: string | string[]
  }): boolean {
    return this.getPurchaseOptions(course).some((option) => !option.disabled);
  }

  public getPurchaseOptions(course?: {
    id: string,
    name: string,
    access: boolean,
    price: number,
    bookPrice?: number,
    bundlePrice?: number,
    hasCoursePurchase?: boolean,
    hasBookPurchase?: boolean,
    hasBundlePurchase?: boolean,
    bookPdf?: string | string[],
    bookPreviewPdf?: string | string[]
  }): PurchaseOption[] {
    if (!course) {
      return [];
    }

    const options: PurchaseOption[] = [];
    const hasBook = this.hasBookPdf(course.bookPdf);
    const previewUrl = this.getBookPreviewUrl(course);
    const hasCoursePurchase = !!course.hasCoursePurchase || !!course.access;
    const hasBookPurchase = !!course.hasBookPurchase;
    const hasBundlePurchase = !!course.hasBundlePurchase;

    if (this.isPositivePrice(course.price)) {
      const courseDisabled = hasCoursePurchase || course.access;
      options.push({
        type: 'course',
        title: `Kurs: ${course.name}`,
        description: `Zugang für den ${course.name}-Kurs mit dem gesamten Fragenkatalog.`,
        price: course.price,
        cta: hasCoursePurchase ? 'Gekauft' : (course.access ? 'Freigeschaltet' : 'Kurs kaufen'),
        disabled: courseDisabled
      });
    }

    if (hasBook && this.isPositivePrice(course.bookPrice)) {
      const bookPurchased = hasBookPurchase || hasBundlePurchase;
      options.push({
        type: 'book',
        title: `Buch: ${course.name} (PDF)`,
        description: `Das begleitende digitale Buch zu ${course.name}.`,
        price: course.bookPrice,
        cta: bookPurchased ? 'Gekauft' : 'Buch kaufen',
        disabled: bookPurchased,
        previewUrl
      });
    }

    if (hasBook && this.isPositivePrice(course.bundlePrice) && this.isPositivePrice(course.price)) {
      const bundleDisabled = hasBundlePurchase || hasCoursePurchase || hasBookPurchase;
      options.push({
        type: 'bundle',
        title: 'Bundle: Kurs + Buch',
        description: `Kurs und PDF-Buch zu ${course.name} im Bundle.`,
        price: course.bundlePrice,
        cta: hasBundlePurchase ? 'Gekauft' : (bundleDisabled ? 'Nicht verfugbar' : 'Bundle kaufen'),
        disabled: bundleDisabled,
        previewUrl
      });
    }

    return options;
  }

  public async downloadBook(course?: { id: string, hasBookAccess?: boolean, bookPdf?: string | string[] }) {
    if (!course?.hasBookAccess || !course?.bookPdf) {
      return;
    }

    const fileName = this.extractFileName(course.bookPdf);
    if (!fileName) {
      return;
    }

    try {
      // Generate URL with fresh token at download time
      const downloadUrl = await this.pocketbaseService.getCourseFileUrl(course.id, fileName, true);
      
      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Failed to download book:', error);
      alert('Der Download konnte leider nicht gestartet werden. Bitte versuche es später erneut.');
    }
  }

}
